// useUniversalNotify.ts
import type { KnownBlock } from '@slack/types'
import { convertHtmlToPlainText, extractMentionIdsFromHtml } from 'components/Comments/utils/formatHtml'
import { useLocation } from 'react-router-dom'
import { Notify } from 'types'
import { useSnackbarContext } from '../../../context/SnackbarProvider/SnackbarProvider'
import { useSlackNotification } from '../../slack/useSlackNotification'
import { useCreateNotification } from '../useCreateNotification'
import { actions, button, context, divider, replaceInBlocks, section } from './slackBlocks'
import {
  baseListUrlAbsolute,
  baseListUrlRelative,
  baseTitle,
  buildTaskUrlAbsolute,
  buildTaskUrlRelative,
  detectRouteBase,
} from './taskNotifyHelpers'

const isProd = import.meta.env.VITE_ENV === 'production'
const APP_BASE = isProd ? 'https://grobotic.app' : 'https://dev.grobotic.app'

export const useUniversalNotify = () => {
  const { mutate: sendSlackMessage } = useSlackNotification()
  const { mutate: createNotification } = useCreateNotification()
  const { showSnackbar } = useSnackbarContext()
  const location = useLocation()

  const notify = async ({ data, dry_run = false }: { data: Notify; dry_run?: boolean }) => {
    let {
      users,
      tagIds,
      type: _type,
      severity: _severity,
      title: _title,
      message,
      board: _board,
      boardItemName,
      boardId: _boardId,
      slackHeaderMessage,
      slackMessageHtml,
      customSlackMessageHtml,
      customSlackBlocks,
      sendSlack,
      sendApp,
      slackDirectLink,
      includeTaskLink,
      task,
      routeBase, // NEW (optional)
      links, // in-app link list (optional)
      url, // in-app default click target (optional)
    } = data ?? {}

    try {
      // Resolve recipients from tagIds/HTML mentions when needed
      if ((!users || users.length === 0) && Array.isArray(tagIds) && tagIds.length) {
        users = tagIds.map(id => ({ id } as any))
      }
      if ((!users || users.length === 0) && (slackMessageHtml || message)) {
        const scraped = extractMentionIdsFromHtml(slackMessageHtml || message)
        if (scraped.length) users = scraped.map(id => ({ id } as any))
      }
      if (!users?.length) return

      if (dry_run) {
        console.warn('[notify] dry run')
        console.log(data)
        return
      }

      // Determine base from caller override or current location
      const base = routeBase ?? detectRouteBase(location.pathname)
      const baseName = baseTitle(base)

      // Build task URLs (if a single task context is provided)
      const isSubtask = task?.SK?.startsWith('SUBTASK_')
      const absTaskUrl = task?.PK && task?.SK ? buildTaskUrlAbsolute(APP_BASE, base, task.PK, task.SK) : null
      const relTaskUrl =
        task?.PK && task?.SK
          ? !isSubtask
            ? buildTaskUrlRelative(base, task.PK, task.SK)
            : `/${base}/${task.PK}/${task.parentTaskSK}/subtasks`
          : null

      const senderId = localStorage.getItem('slack_user_id') || ''
      const plainTextMessage = convertHtmlToPlainText(slackMessageHtml || message)
      const slackBaseUrl = import.meta.env.VITE_BASE_URL || '' // your app origin for deep links back

      // 1) In-app notifications
      if (sendApp) {
        const appNotifications = users.map(user => ({
          ...data,
          routeBase: base, // keep so the drawer knows context if needed
          userId: (user as any).id || (user as any).userId,
          content: plainTextMessage ?? null,
          imageUrl: (user as any).imageUrl || (user as any).image_512 || '',
          // Default click target: respect caller's url override -> else task -> else current details -> else base list
          url: url ?? (includeTaskLink && relTaskUrl ? relTaskUrl : `${location.pathname}/details`),
          links, // if provided: [{label,url}, ...] (relative routes)
        }))
        appNotifications.forEach(notification => {
          createNotification(notification, {
            onSuccess: () => console.log('✅ App notification created', notification),
            onError: err => console.error('❌ Failed to create app notification:', err),
          })
        })
      }

      // 2) Slack notifications (typed blocks)
      if (sendSlack) {
        const slackIdPlaceholder = '<@{SLACK_ID}>'
        const baseMessage = convertHtmlToPlainText(customSlackMessageHtml || slackMessageHtml || message)

        const defaultBlocks: KnownBlock[] = [
          section(`Hi ${slackIdPlaceholder}, ${slackHeaderMessage}\nTagged by: <@${senderId}>`),
          divider(),
          section(`*Message:* ${baseMessage}`),
          ...(slackDirectLink
            ? [
                section(
                  `*<${slackBaseUrl}${slackDirectLink}|View Details>*${
                    includeTaskLink && absTaskUrl ? `\n*<${absTaskUrl}|Related Task>*` : ''
                  }`
                ),
              ]
            : []),
        ]

        const blocks: KnownBlock[] =
          Array.isArray(customSlackBlocks) && customSlackBlocks.length > 0 ? customSlackBlocks : defaultBlocks

        users.forEach(user => {
          const slackId = (user as any).id || (user as any).slackId || (user as any).user_uuid
          if (!slackId) return
          const blocksForUser = replaceInBlocks(blocks, '{SLACK_ID}', slackId)
          sendSlackMessage({
            slackUserIds: [slackId],
            blocks: blocksForUser,
            text: `You've been tagged in ${boardItemName ?? baseName}`,
            icon_emoji: ':bell:',
          })
        })
      }

      console.log('✅ Notification(s) sent successfully.')
    } catch (err) {
      console.error('❌ Failed to send notifications:', err)
      showSnackbar('Failed to send notifications', 'error')
    }
  }

  /**
   * Batch “active assignee” notifications: one message per user with N links.
   * `routeBase` is auto-detected but can be overridden (e.g. 'projects').
   */
  const notifyAssignments = async ({
    assignmentsByUser,
    headerText = 'You were set as Active Assignee',
    maxLinks = 5,
    sendApp = true,
    sendSlack = true,
    routeBase, // optional override
    role_assignment = 'Active Assignee',
  }: {
    assignmentsByUser: Record<string, { PK: string; SK: string; name?: string }[]>
    headerText?: string
    maxLinks?: number
    sendApp?: boolean
    sendSlack?: boolean
    routeBase?: 'teamtasks' | 'projects'
    role_assignment?: 'Active Assignee' | 'Assignee'
  }) => {
    const base = routeBase ?? detectRouteBase(location.pathname)
    const baseName = baseTitle(base)

    const renderSlackLines = (tasks: { PK: string; SK: string; name?: string }[]) => {
      const top = tasks.slice(0, maxLinks)
      const lines = top
        .map((t, i) => `${i + 1}. <${buildTaskUrlAbsolute(APP_BASE, base, t.PK, t.SK)}|${t.name || t.SK}>`)
        .join('\n')
      const overflow = Math.max(0, tasks.length - top.length)
      return { lines, overflow, top }
    }

    Object.entries(assignmentsByUser).forEach(([userId, tasks]) => {
      if (!tasks?.length) return
      const count = tasks.length
      const { lines, overflow, top } = renderSlackLines(tasks)

      // Slack blocks
      const blocks: KnownBlock[] = [
        section(`Hi <@${userId}>, ${headerText} on *${count}* task${count > 1 ? 's' : ''}.`),
        divider(),
        section(lines || '_No links available._'),
        ...(overflow > 0 ? [context([`…and *${overflow}* more.`])] : []),
        actions([button(`Open ${baseName}`, baseListUrlAbsolute(APP_BASE, base))]),
      ]

      // In-app links (relative)
      const inAppLinks = top.map((t, i) => ({
        label: `${i + 1}. ${t.name || t.SK}`,
        url: buildTaskUrlRelative(base, t.PK, t.SK),
      }))

      notify({
        data: {
          tagIds: [userId],
          routeBase: base, // keep for downstream
          type: 'assignment',
          severity: 'info',
          title: `${role_assignment} update`,
          message:
            `You were set as ${role_assignment} on ${count} task${count > 1 ? 's' : ''}.` +
            (overflow ? ` (+${overflow} more)` : ''),
          board: base,
          boardItemName: baseName,
          boardId: base,
          customSlackBlocks: blocks,
          slackMessageHtml: lines, // plain text in-app fallback
          slackDirectLink: baseListUrlRelative(base),
          sendSlack,
          sendApp,
          url: baseListUrlRelative(base), // default card click target
          links: inAppLinks, // per-task links
        },
      })
    })
  }

  return { notify, notifyAssignments }
}
